import {
  FULL_HD,
  HD,
  RENDERING_CONTEXT,
  SCREEN_WIDTH,
  SCREEN_HEIGHT,
  SIDENAV_WIDTH,
  LAYOUT_LEFT_PADDING,
  safetyMargins,
  CONTENT_GAP,
  KEYBOARD_WIDTH,
} from '@bedrock-rdk/constants';

export { SCREEN_WIDTH, SCREEN_HEIGHT };

/**
 * Lightning template function that allows to inherit parent's size
 *
 * @param {number} value value to passthrough, parent's size(with or height)
 * @returns {number} the same value
 */
export const identity = value => value;

/**
 * Creates Lightning template function that allows to inherit parent's size with a modifier
 *
 * @param {number} percent value which will be multiplied by parent's size, usually a number between 0 and 1
 * @returns {Function} size calculation function, multiplies parent's dimension by a percentage
 */
export const proportional = percent => value => Math.round(value * percent);

/**
 * Lightning template function that divides given value by 2
 *
 * @param {number} value value to passthrough, parent's size(with or height)
 * @returns {number} original value, divided by 2 and rounded to a nearest integer
 */
export const middle = proportional(0.5);

/**
 * Checks if window height is lower or equal to HD resolution(720px) and returns a corresponding string
 *
 * @returns {('HD'|'FULL_HD')}
 */
export const getScreenSize = () => (window.innerHeight <= 720 ? HD : FULL_HD);

/**
 * Calculates middle point basing on parent and client sizes
 *
 * @param {number} parentSize either width or height of a parent
 * @param {number} childSize either width or height of a child
 * @returns {number} center point, rounded to nearest integer
 */
export const getCenter = (parentSize, childSize) => Math.round((parentSize - childSize) / 2);

export const getElementSize = (itemsByBreakpoints, ratio, templateName) => {
  const _generalItemsByBreakpoints = itemsByBreakpoints.general || itemsByBreakpoints;
  const _searchItemsByBreakpoints = itemsByBreakpoints.search || itemsByBreakpoints;

  const _sizes = [HD, FULL_HD].reduce((_, screenSize) => {
    if (!_generalItemsByBreakpoints[screenSize]) {
      throw new Error(`Items per breakpoint for size ${screenSize} is not defined`);
    }

    const width =
      (SCREEN_WIDTH[screenSize] -
        (SIDENAV_WIDTH[screenSize] + LAYOUT_LEFT_PADDING[screenSize] + safetyMargins.horizontal[screenSize]) -
        CONTENT_GAP[screenSize] * (_generalItemsByBreakpoints[screenSize] - 1)) /
      _generalItemsByBreakpoints[screenSize];
    const height = Math.round(width * ratio);

    return {
      ..._,
      [screenSize]: {
        w: Math.round(width),
        h: height,
      },
    };
  }, {});

  const _searchSizes = [HD, FULL_HD].reduce((_, screenSize) => {
    if (!_searchItemsByBreakpoints[screenSize]) {
      throw new Error(`Search items per breakpoint for size ${screenSize} is not defined`);
    }

    const width =
      (SCREEN_WIDTH[screenSize] -
        (SIDENAV_WIDTH[screenSize] + LAYOUT_LEFT_PADDING[screenSize] + safetyMargins.horizontal[screenSize] * 2) -
        CONTENT_GAP[screenSize] * (_searchItemsByBreakpoints[screenSize] - 1) -
        KEYBOARD_WIDTH[screenSize]) /
      _searchItemsByBreakpoints[screenSize];
    const height = Math.round(width * ratio);

    return {
      ..._,
      [screenSize]: {
        w: Math.round(width),
        h: height,
      },
    };
  }, {});

  const _imageOptions = [HD, FULL_HD].reduce(
    (_, screenSize) => ({
      ..._,
      [screenSize]: {
        PRIMARY: {
          width: _sizes[screenSize].w,
          height: _sizes[screenSize].h,
        },
      },
    }),
    {},
  );

  const _searchImageOptions = [HD, FULL_HD].reduce(
    (_, screenSize) => ({
      ..._,
      [screenSize]: {
        PRIMARY: {
          width: _searchSizes[screenSize].w,
          height: _searchSizes[screenSize].h,
        },
      },
    }),
    {},
  );

  return {
    sizes: {
      [templateName]: {
        [RENDERING_CONTEXT.GENERAL]: _sizes,
        [RENDERING_CONTEXT.SEARCH]: _searchSizes,
      },
    },
    imageOptions: {
      [templateName]: {
        [RENDERING_CONTEXT.GENERAL]: _imageOptions,
        [RENDERING_CONTEXT.SEARCH]: _searchImageOptions,
      },
    },
  };
};
